import { Box, IconButton } from '@mui/material';
import Restaurant from '../../../app/data/models/restaurant';
import { useEffect, useState } from 'react';
import RestaurantForm from './RestaurantForm';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import {
  fetchRestaurantListAsync,
  deleteRestaurantAsync,
  getRestaurantSelectors,
} from './restaurantSlice';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import DeleteConfirmationDialog from '../../../app/components/DeleteConfirmationDialog';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import EmptyScreen from '../../../app/components/EmptyScreen';
import { useTranslation } from 'react-i18next';
import ListTitleBox from '@app/components/list/ListTitleBox';
import RestaurantListItem from '@app/components/restaurantList/RestaurantListItem';

const RestaurantList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const restaurantSelectors = getRestaurantSelectors();
  const restaurants: Restaurant[] = useAppSelector(
    restaurantSelectors.selectAll
  );
  const { restaurantsLoaded, status, nextPageId } = useAppSelector(
    (state) => state.restaurants
  );
  const { user } = useAppSelector((state) => state.account);
  const [editMode, setEditMode] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<
    Restaurant | undefined
  >(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchRestaurantListAsync(nextPageId));
  }, [nextPageId, dispatch]);

  const handleEditRestaurant = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setEditMode(true);
  };

  const handleSelectRestaurant = (restaurant: Restaurant) => {
    navigate(`/manager/restaurants/${restaurant.id}`);
  };

  const cancelEdit = () => {
    if (selectedRestaurant) setSelectedRestaurant(undefined);
    setEditMode(false);
  };

  const handleDeleteClick = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedRestaurant) {
      try {
        await dispatch(deleteRestaurantAsync(selectedRestaurant.id)).unwrap();
        setDeleteDialogOpen(false);
        setSelectedRestaurant(undefined);
        dispatch(fetchRestaurantListAsync(undefined));
        toast.success(t('restaurant.delete.success'));
      } catch (error) {
        console.error('Error deleting restaurant:', error);
        toast.error(t('common.error'));
      }
    }
  };

  if (status.includes('pending') && !restaurantsLoaded)
    return <LoadingComponent />;

  if (editMode)
    return (
      <RestaurantForm restaurant={selectedRestaurant} cancelEdit={cancelEdit} />
    );

  if (restaurants.length === 0 && restaurantsLoaded)
    return (
      <EmptyScreen
        title={t('restaurant.list.title')}
        emptyText={t('restaurant.list.empty')}
        onPrimaryClick={
          user?.roles?.isSuperAdmin || false
            ? () => setEditMode(true)
            : undefined
        }
      />
    );

  return (
    <>
      <ListTitleBox
        title={t('restaurant.list.title')}
        onPrimaryClick={
          user?.roles?.isSuperAdmin || false
            ? () => setEditMode(true)
            : undefined
        }
      />
      <Box sx={{ width: '100%', mb: 2, px: { xs: 1, sm: 2 } }}>
        {restaurants.map((restaurant) => (
          <RestaurantListItem
            key={restaurant.id}
            restaurant={restaurant}
            isEditable={true}
            roles={user?.roles}
            onClick={() => handleSelectRestaurant(restaurant)}
            onEdit={() => handleEditRestaurant(restaurant)}
            onDelete={() => handleDeleteClick(restaurant)}
            loading={
              status.includes('pendingDelete') &&
              selectedRestaurant?.id === restaurant.id
            }
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2, gap: 2 }}>
        <IconButton
          onClick={() => dispatch(fetchRestaurantListAsync(undefined))}
          disabled={!nextPageId || status.includes('pending')}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => dispatch(fetchRestaurantListAsync(nextPageId))}
          disabled={!nextPageId || status.includes('pending')}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('restaurant.delete.title')}
        confirmText={t('common.delete')}
        itemName={selectedRestaurant?.name || ''}
      />
    </>
  );
};

export default RestaurantList;
